import React, { useState, useEffect } from 'react';
import { X, MousePointer2, Undo2, Save, Sparkles, FolderOpen, Download } from 'lucide-react';

const KEY = 'pm_editor_tour_seen_v1';

const STEPS = [
  { icon: MousePointer2, title: 'Welcome to the Editor',       body: 'Click any object in the scene to select it. Drag the on-screen gizmo to move, rotate or scale.' },
  { icon: Undo2,         title: 'Undo any change',             body: 'Every mutation goes on a 30-step undo stack. Hit ↶ Undo in the header to roll back.' },
  { icon: Save,          title: 'Save + Revert',               body: 'Save creates a server-side checkpoint. ⟲ Revert rolls back to your last saved state — with a pre-revert safety snapshot.' },
  { icon: Sparkles,      title: 'AI Assets Panel',             body: 'Right sidebar → Assets. Generate an RPG asset, remix it, batch-pack a whole theme, and drag any thumbnail onto the scene.' },
  { icon: FolderOpen,    title: 'Your Library',                body: 'Uploads (images, audio, video, model files) live at /library. Drag files from Library into the Editor to spawn them as scene objects.' },
  { icon: Download,      title: 'Export anywhere',             body: 'Export ▾ in the header — playable HTML5, raw JSON, Godot 4 (.tscn), or Unity C# (.cs). Godot texture loader is one click away.' },
];

/**
 * 6-step Editor guided tour. Shown once ever on first Editor open.
 * "Show me again" from Settings clears the key to re-run it.
 */
export default function EditorTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { if (localStorage.getItem(KEY)) return; } catch { /* ignore */ }
    // Small delay so scene loads first
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;
  const S = STEPS[step];
  const Icon = S.icon;

  const dismiss = () => {
    try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid="editor-tour"
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-md bg-[#0A0A0B] border border-[#1E1E24] rounded-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-zinc-500 hover:text-white z-[61]"
          aria-label="Dismiss tour"
          data-testid="editor-tour-close"
        >
          ✕
        </button>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#06B6D4]/15 border border-[#06B6D4]/30 flex items-center justify-center shrink-0">
            <Icon className="w-7 h-7 text-[#06B6D4]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Step {step + 1} of {STEPS.length}</div>
            <h3 className="text-lg font-bold text-white mb-1">{S.title}</h3>
            <p className="text-sm text-zinc-400">{S.body}</p>
          </div>
          <button onClick={dismiss} className="text-zinc-500 hover:text-white" aria-label="Skip tour">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#06B6D4]' : i < step ? 'w-1.5 bg-[#06B6D4]/50' : 'w-1.5 bg-white/20'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="text-sm text-zinc-400 hover:text-white" data-testid="editor-tour-back">Back</button>
            )}
            {step < STEPS.length - 1 ? (
              <button onClick={() => setStep(step + 1)} className="text-sm font-semibold bg-[#06B6D4] text-black px-4 py-1.5 rounded-full hover:bg-[#06B6D4]/90" data-testid="editor-tour-next">
                Next →
              </button>
            ) : (
              <button onClick={dismiss} className="text-sm font-semibold bg-[#06B6D4] text-black px-4 py-1.5 rounded-full hover:bg-[#06B6D4]/90" data-testid="editor-tour-done">
                Start building
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
